import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import Layout from '../../shared/Layout';
import PageHeader from '../../shared/PageHeader';
import IconGoBack from '../../../components/icons/IconGoBack';
import useStudentWorkspaceData from './useStudentWorkspaceData';
import WorkspaceOutline from './WorkspaceOutline';
import WorkspaceTopicSection from './WorkspaceTopicSection';
import '../../../css/courses.css';
import '../../../css/course-preview.css';
import '../../../css/container-layout.css';
import '../../../css/workspace-activity.css';

// Teacher-side "what did this student actually do in this course" view -
// opened by clicking a row in StudentCourses.jsx (the student card's
// "Курси" tab). Layout mirrors courses/Workspace.jsx's 2-column shell
// (same CSS classes, same tree-col/detail-col split) but the right side
// always shows every topic's activity in one continuous scroll rather than
// filtering to whatever's selected - the whole point here is reviewing
// everything at once, not authoring one piece at a time.
export default function StudentWorkspaceView() {
   const { studentId, courseId } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const data = useStudentWorkspaceData(studentId, courseId);
   const [activeTopicId, setActiveTopicId] = useState(null);
   const sectionElsRef = useRef(new Map());
   const inViewRef = useRef(new Set());
   const scrollColRef = useRef(null);

   const topics = data.status === 'ready' ? data.topics : [];

   // Brief highlight whenever a live WS event just refreshed something
   // (data.lastLiveUpdateAt bumped by useStudentWorkspaceData.js) - purely
   // a "yes, this really did just update itself" cue, not load-bearing.
   const [livePulse, setLivePulse] = useState(false);
   const lastSeenUpdateRef = useRef(null);
   useEffect(() => {
      if (data.status !== 'ready' || !data.lastLiveUpdateAt) return undefined;
      if (data.lastLiveUpdateAt === lastSeenUpdateRef.current) return undefined;
      lastSeenUpdateRef.current = data.lastLiveUpdateAt;
      setLivePulse(true);
      const t = setTimeout(() => setLivePulse(false), 2200);
      return () => clearTimeout(t);
   }, [data.status, data.lastLiveUpdateAt]);

   useEffect(() => {
      if (data.status !== 'ready' || topics.length === 0) return undefined;
      const orderedIds = topics.map(t => t.topic.id);
      const observer = new IntersectionObserver((entries) => {
         entries.forEach(entry => {
            const topicId = Number(entry.target.dataset.topicId);
            if (entry.isIntersecting) inViewRef.current.add(topicId);
            else inViewRef.current.delete(topicId);
         });
         const active = orderedIds.filter(id => inViewRef.current.has(id)).pop();
         if (active != null) setActiveTopicId(active);
      }, { rootMargin: '0px 0px -65% 0px', threshold: 0 });
      sectionElsRef.current.forEach(el => { if (el) observer.observe(el); });
      return () => observer.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.status, topics.length]);

   const registerSectionEl = (topicId, el) => {
      if (el) sectionElsRef.current.set(topicId, el);
      else sectionElsRef.current.delete(topicId);
   };

   const jumpToCourse = () => { if (scrollColRef.current) scrollColRef.current.scrollTo({ top: 0, behavior: 'smooth' }); };
   const jumpToTopic = (topicId) => {
      const el = sectionElsRef.current.get(topicId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
   };

   // LessonTopicFlyout.jsx's pills link straight here with a `?topic=` id
   // (a lesson can only know a topic's id, not this page's own scroll
   // refs) - jump to that section once its ref is actually registered.
   // Tried twice (immediately, then after a short delay) since the section
   // refs populate during the same render pass as `topics` first becoming
   // non-empty, which can race this effect on a slow paint.
   const jumpedToQueryTopicRef = useRef(false);
   useEffect(() => {
      if (jumpedToQueryTopicRef.current) return;
      if (data.status !== 'ready' || topics.length === 0) return;
      const topicParam = new URLSearchParams(location.search).get('topic');
      if (!topicParam) { jumpedToQueryTopicRef.current = true; return; }
      const targetId = Number(topicParam);
      const tryJump = () => {
         const el = sectionElsRef.current.get(targetId);
         if (el) {
            jumpedToQueryTopicRef.current = true;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
         }
      };
      tryJump();
      const t = setTimeout(tryJump, 400);
      return () => clearTimeout(t);
   }, [data.status, topics.length, location.search]);

   const backToStudent = () => navigate('/students/' + studentId);

   return (
      <Layout>
         <PageHeader name={
            <div className='course-workspace-header-inner'>
               <div className='course-workspace-back-btn' onClick={backToStudent} title='До картки студента'>
                  <IconGoBack />
               </div>
               <span className='course-workspace-title'>
                  {data.status === 'ready' ? data.course.name : 'Активність студента'}
               </span>
            </div>
         }>
            {data.status === 'ready' ? (
               <div className='workspace-stats-strip'>
                  <span className={'workspace-presence-indicator' + (data.studentOnline ? ' online' : '')}
                     title={data.studentOnline ? 'Студент зараз відкрив цей курс на платформі' : 'Студент зараз не на платформі'}>
                     <i className='workspace-presence-dot' />
                     {data.studentOnline ? 'Студент онлайн' : 'Студент офлайн'}
                  </span>
                  <span className={'workspace-live-indicator' + (livePulse ? ' active' : '')} title='Оновлюється в реальному часі'>
                     <i className='workspace-live-dot' />
                     Наживо
                  </span>
                  <span className='workspace-stat-pill'><b>{data.activityStats.EXERCISE_ANSWER}</b> відповідей</span>
                  <span className='workspace-stat-pill'><b>{data.activityStats.CONVERSATION_STEP}</b> кроків розмов</span>
                  <span className='workspace-stat-pill'><b>{data.activityStats.NOTE_ADDED}</b> нотаток</span>
                  {data.workspace.lastActivityAt ? (
                     <span className='workspace-stat-pill workspace-stat-pill-last'>
                        Остання активність: {DateTime.fromISO(data.workspace.lastActivityAt).toFormat('dd.MM.yyyy HH:mm')}
                     </span>
                  ) : ''}
               </div>
            ) : ''}
         </PageHeader>
         <main className='main-block'>
            <div className='main-content txt-c course-workspace-page'>
               {data.status === 'loading' ? (
                  <div className='event-edit-loading'>Завантаження...</div>
               ) : data.status === 'not-started' ? (
                  <div className='workspace-not-started'>
                     <p>Студент ще не відкривав цей курс на платформі.</p>
                  </div>
               ) : data.status === 'error' ? (
                  <div className='event-edit-loading'>Не вдалося завантажити дані.</div>
               ) : (
                  <div className='course-workspace-body'>
                     <div className='course-workspace-tree-col'>
                        <WorkspaceOutline course={data.course} topics={topics} activeTopicId={activeTopicId}
                           onJumpToCourse={jumpToCourse} onJumpToTopic={jumpToTopic} />
                     </div>
                     <div className='course-workspace-detail-col' ref={scrollColRef}>
                        <div className='course-preview'>
                           {topics.length === 0 ? (
                              <div className='course-preview-empty'>Тут ще немає жодної теми з контентом.</div>
                           ) : topics.map(topicEntry => (
                              <WorkspaceTopicSection key={topicEntry.topic.id} topicEntry={topicEntry}
                                 notesByTarget={data.notesByTarget} progressByElementId={data.progressByElementId}
                                 savedStateByElementId={data.savedStateByElementId} workspaceId={data.workspace.id}
                                 studentId={studentId} courseId={courseId} onNotesChanged={data.refreshNotes}
                                 markersByTarget={data.markersByTarget} onMarkersChanged={data.refreshMarkers}
                                 homeworkByExerciseTemplateId={data.homeworkByExerciseTemplateId}
                                 sectionRef={(el) => registerSectionEl(topicEntry.topic.id, el)} />
                           ))}
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </main>
      </Layout>
   );
}
